/* @flow */

import React, { useContext } from "react";
import cn from "classnames";
import { useData } from "crustate/react";
import { Link } from "react-router-dom";
import { HomeData } from "data";
import { StoreInfoContext } from "entrypoint/shared";
import { useBrowser } from "@awardit/react-use-browser";
import { Carousel, Combined, Dots } from "@crossroads/ui-components";
import { Helmet } from "react-helmet-async";
import { useTranslate } from "@awardit/react-use-translate";
import useDevice from "helpers/use-device";
import useBrowserDimensions from "helpers/use-browser-dimensions";
import { formatMeta } from "helpers/get-meta";
import Wrapper from "components/Wrapper";
import Button from "components/Button";
import ProductList from "components/ProductList";
import Tutorial from "components/Tutorial";
import PopularBrands from "components/PopularBrands";
import ArrowIcon from "icons/arrow.svg";
import Hero from "components/Hero";

import styles from "./styles.scss";
import { useViewMode } from "context/view-mode";

type Block = {
  name: string,
  image?: { x1?: string, x2?: string },
  url: string,
  description: string,
};

type CarouselBlock = {
  description: string | null,
  image1x: (void | string),
  image2x: (void | string),
  name: string,
  url: string,
};

const enrichImagesData = (data: any) => {
  return data.map(s => ({
    ...s,
    images: [
      {
        media: "(max-width: 550px)",
        url: s.images.small ? `${s.images.small.x1} 1x, ${s.images.small.x2} 2x` : null,
        dataUrl: s.images.small ? s.images.small.x1 : null,
      },
      {
        media: "(min-width: 551px)",
        url: s.images.large ? `${s.images.large.x1} 1x, ${s.images.large.x2} 2x` : null,
        dataUrl: s.images.large ? s.images.large.x1 : null,
      },
    ],
  }));
};

const HomeView = (): React$Node => {
  const browser = useBrowser();
  const t = useTranslate();
  const data = useData(HomeData);
  const { info } = useContext(StoreInfoContext);
  const isDesktop = useDevice("gt", 550);
  const { width: browserWidth } = useBrowserDimensions();

  const [, setMode] = useViewMode();

  if (data.state !== "LOADED") {
    return null;
  }

  const popularProducts = data.data.featuredProducts ?
    data.data.featuredProducts.category.products.items :
    [];

  const slideData = (Object.values(data.data): Array<any>)
    // $FlowIssue[method-unbinding]
    .filter(e => e && Object.prototype.hasOwnProperty.call(e, "carousel"))
    .map((c: { carousel: Block }) => ({
      name: c.carousel.name,
      url: c.carousel.url,
      description: null,
      image1x: c.carousel.image && c.carousel.image.x1,
      image2x: c.carousel.image && c.carousel.image.x2,
    }));

  const categorySlides = slideData.map((s: CarouselBlock, i: number) => (
    <Link
      key={s.name}
      className={styles.carouselItem}
      style={{ display: "block" }}
      data-index-number={i + 1}
      to={{
        pathname: s.url,
        state: { hint: { type: "category", category: { name: s.name } } },
      }}
    >

      <picture className={styles.image}>
        <source srcSet={`${s.image1x ?? ""} 1x, ${s.image2x ?? ""} 2x`} />
        <img alt="" src={s.image1x} />
      </picture>

      <div className={styles.titleWrapper}>
        <h3 className={styles.title}>{s.name}</h3>
        <p>{s.description}</p>
      </div>
    </Link>
  ));

  const heroData = (Object.values(data.data): Array<any>)
    // $FlowIssue[method-unbinding]
    .filter(e => e && Object.prototype.hasOwnProperty.call(e, "block"))
    .map(c => ({
      heading: c.block.heading,
      description: c.block.text,
      url: c.block.url,
      images: {
        small: {
          x1: c.block.smallImage && c.block.smallImage.x1,
          x2: c.block.smallImage && c.block.smallImage.x2,
        },
        large: {
          x1: c.block.largeImage && c.block.largeImage.x1,
          x2: c.block.largeImage && c.block.largeImage.x2,
        },
      },
      button: {
        text: c.block.cta,
        link: c.block.link,
      },
    }));

  const meta = formatMeta({
    title: info.defaultTitle,
    description: info.defaultDescription,
    canonicals: [{ rel: "canonical", href: info.baseUrl }],
  });

  const visiblePopularProducts = () => {
    if (browserWidth < Number.parseInt(styles.small, 10)) {
      return 2;
    }

    if (browserWidth < Number.parseInt(styles.medium, 10)) {
      return 3;
    }

    return 4;
  };

  const itemsPerRow = () => {
    if (browserWidth < Number.parseInt(styles.small, 10)) {
      return 2;
    }

    if (browserWidth < Number.parseInt(styles.medium, 10)) {
      return 3;
    }

    return 4;
  };

  return (
    <div className={styles.block}>
      <Helmet
        title={meta.title}
        meta={meta.data}
        link={meta.link}
      />

      <h1 className="sr-only">{info.titlePrefix}</h1>

      {heroData && heroData.length > 0 &&
        <Wrapper className={styles.topBlock}>
          <Hero data={enrichImagesData(heroData)[0]} />
        </Wrapper>
      }

      <Wrapper>
        <section id="about" className={styles.about}>
          <h2 className={cn(styles.heading, styles.headingTutorial)}>
            {t("HOMEVIEW.TUTORIAL")}
          </h2>
          <Tutorial />
        </section>
      </Wrapper>

      <Wrapper>
        <section className={styles.popularCategories}>
          <div className={styles.headingSplit}>
            <h2 className={styles.heading}>{t("HOMEVIEW.POPULAR_CATEGORIES")}</h2>

            <Button
              variant="blank"
              className={styles.cta}
              onClick={() => setMode("categories")}
            >
              {t("HOMEVIEW.ALL_CATEGORIES")}
            </Button>
          </div>

          <div className={styles.carouselWrapper}>
            <Carousel
              className={styles.carousel}
              slidesToScroll={itemsPerRow()}
              slidesToShow={itemsPerRow()}
              items={categorySlides}
            >
              <div
                className={styles.controls}
                role="group"
                aria-label={t("CAROUSEL.CONTROLS.LABEL")}
              >
                {isDesktop ? (
                  <Combined
                    icon={<ArrowIcon aria-label={t("ICONS.ARROW")} />}
                    className={styles.carouselControls}
                    type="alt"
                  />
                ) : (
                  <Dots className={styles.carouselControls} type="alt" />
                )}
              </div>
            </Carousel>
          </div>
        </section>
      </Wrapper>

      <Wrapper>
        <PopularBrands />
      </Wrapper>

      {(browser && Array.isArray(popularProducts) && popularProducts.length > 0) &&
        <Wrapper>
          <div className={styles.popularProducts}>
            <div className={styles.headingSplit}>
              <h2 className={styles.heading}>{t("HOMEVIEW.POPULAR_PRODUCTS")}</h2>
              {data.data.featuredProducts &&
                <Link
                  variant="blank"
                  className={styles.cta}
                  to={{
                    pathname: data.data.featuredProducts.category.url,
                    state: { hint: {
                      type: "category",
                      category: {
                        name: "Populära produkter",
                      },
                    } },
                  }}
                >
                  {t("HOMEVIEW.MORE_POPULAR_PRODUCTS")}
                </Link>
              }
            </div>
            <ProductList
              className={styles.products}
              products={popularProducts.slice(0, visiblePopularProducts())}
              listName={t("HOMEVIEW.POPULAR_PRODUCTS")}
            />
          </div>
        </Wrapper>
      }
    </div>
  );
};

export default HomeView;
